<template>
    <div class="flex min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      <!-- Sidebar -->
      <HrSidebar :isOpen="isSidebarOpen" />
  
      <!-- Main Content -->
      <main 
        class="flex-1 overflow-x-hidden transition-all duration-300"
        :class="{'ml-64': isSidebarOpen, 'ml-0': !isSidebarOpen}"
      >
        <!-- Navbar -->
             <header 
        class="fixed top-0 z-50 w-full bg-white dark:bg-gray-800 shadow-sm transition-all duration-300"
      >
        <HrTopbar 
          :page-title="currentPageTitle"
          :is-sidebar-open="isSidebarOpen"
          :is-dark-mode="isDarkMode"
          @toggle-sidebar="toggleSidebar"
          @toggle-dark-mode="toggleDarkMode"
        />
      </header>
  
        <!-- Content Area -->
        <div class="p-6 max-w-full">
          <div class="flex justify-between items-center mb-8">
            <div class="flex items-center space-x-4">
              <span class="text-gray-600 dark:text-gray-300">Last updated: {{ currentDate }}</span>
              <button 
                @click="refreshData"
                class="p-2 rounded-full bg-white dark:bg-gray-700 shadow hover:bg-gray-100 dark:hover:bg-gray-600"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-600 dark:text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            </div>
          </div>

  
          <!-- Stats Cards -->
          <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8 p-4">
            <!-- Total Staff -->
            <div class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md border-l-4 border-[#035a76]">
              <div class="flex justify-between items-center">
                <div>
                  <p class="text-sm font-medium text-[#035a76] dark:text-[#035a76]">Total Staff</p>
                  <p class="text-2xl font-bold text-[#035a76] dark:text-[#035a76] mt-1">{{ applicants.length }}</p>
                </div>
                <div class="p-3 rounded-full bg-blue-100 dark:bg-[#035a76]/50 text-[#035a76] dark:text-[#035a76]">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
              </div>
              <p class="text-xs text-[#035a76] dark:text-[#035a76] mt-2">All applicants</p>
            </div>
  
            <!-- Hired -->
            <div class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md border-l-4 border-green-500">
              <div class="flex justify-between items-center">
                <div>
                  <p class="text-sm font-medium text-[#035a76] dark:text-[#035a76]">Hired</p>
                  <p class="text-2xl font-bold text-[#035a76] dark:text-[#035a76] mt-1">{{ countStatus('Hired') }}</p>
                </div>
                <div class="p-3 rounded-full bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-400">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
              <p class="text-xs text-[#035a76] dark:text-[#035a76] mt-2">Successfully hired applicants</p>
            </div>
  
            <!-- Rejected -->
            <div class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md border-l-4 border-red-500">
              <div class="flex justify-between items-center">
                <div>
                  <p class="text-sm font-medium text-[#035a76] dark:text-[#035a76]">Rejected</p>
                  <p class="text-2xl font-bold text-[#035a76] dark:text-[#035a76] mt-1">{{ countStatus('Rejected') }}</p>
                </div>
                <div class="p-3 rounded-full bg-red-100 dark:bg-red-900/50 text-red-600 dark:text-red-400">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
              </div>
              <p class="text-xs text-[#035a76] dark:text-[#035a76] mt-2">Rejected applicants</p>
            </div>
  
            <!-- Kept -->
            <div class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md border-l-4 border-yellow-500">
              <div class="flex justify-between items-center">
                <div>
                  <p class="text-sm font-medium text-[#035a76] dark:text-[#035a76]">Kept</p>
                  <p class="text-2xl font-bold text-[#035a76] dark:text-[#035a76] mt-1">{{ countStatus('Kept') }}</p>
                </div>
                <div class="p-3 rounded-full bg-yellow-100 dark:bg-yellow-900/50 text-yellow-600 dark:text-yellow-400">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <p class="text-xs text-[#035a76] dark:text-[#035a76] mt-2">Applicants on hold</p>
            </div>
          </div>
  
<div class="md:col-span-2 p-6 flex justify-end w-full">
  <button 
    v-if="hasPermission('manpower', 'add_applicant')"
    type="submit" 
    class="bg-[#74bd54] hover:bg-[#63a43f] text-white px-10 py-3.5 rounded-2xl font-medium text-lg shadow-lg shadow-[#74bd54]/30 transition-all active:scale-95 flex items-center gap-2"
    @click="showFilter = !showFilter"
    
  >
    <i class="fa fa-plus"></i> New Applicant 
  </button>
</div>
<Transition name="filter">
  <div v-if="showFilter">

    <!-- Add Staff Form -->
    <div class="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-xl space-y-8 mb-8 border border-gray-100 dark:border-gray-700">
      
<!-- Update the form header section -->
<div class="flex items-center justify-between gap-3">
  <div class="flex items-center gap-3">
    <div class="w-10 h-10 bg-secondary dark:bg-blue-600 rounded-xl flex items-center justify-center">
      <span class="text-white text-2xl"><i class="fa fa-user"></i></span>
    </div>
    <h3 class="text-2xl font-semibold text-[#035a76] dark:text-white">
      {{ isEditMode ? 'Edit Applicant' : 'Add New Applicant' }}
    </h3>
  </div>
  
  <!-- Show cancel button-->
  <button 
    @click="cancelEdit"
    type="button"
    class="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
  >
    <i class="fa fa-times mr-1"></i> Close
  </button>
</div>
      
      <form @submit.prevent="addApplicant" class="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-8">

        <!-- Full Name -->
        <div class="md:col-span-2">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Full Name</label>
          <input 
            v-model="newApplicant.name"
            type="text"
            placeholder="Enter full name"
            required
            class="w-full border border-gray-300 dark:border-gray-600 rounded-xl px-4 py-3 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent transition-all"
          />
        </div>

        <!-- Position -->
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Position Applied For</label>
          <select
            v-model="newApplicant.position_id"
            required
            class="w-full border border-gray-300 dark:border-gray-600 rounded-xl px-4 py-3 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent transition-all"
          >
            <option disabled value="">Select Position</option>
            <option v-for="pos in positions" :key="pos.id" :value="pos.id">{{ pos.position_name }}</option>
          </select>
        </div>

        <!-- Email -->
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Email Address</label>
          <input
            v-model="newApplicant.email"
            type="email"
            placeholder="example@email.com"
            required
            class="w-full border border-gray-300 dark:border-gray-600 rounded-xl px-4 py-3 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent transition-all"
          />
        </div>

        <!-- Phone -->
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Phone Number</label>
          <input
            v-model="newApplicant.phone"
            type="tel"
            placeholder="+233 XX XXX XXXX"
            required
            class="w-full border border-gray-300 dark:border-gray-600 rounded-xl px-4 py-3 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent transition-all"
          />
        </div>

        <!-- Status, Type, Period -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6 md:col-span-2">
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Status</label>
            <select v-model="newApplicant.status" 
              class="w-full border border-gray-300 dark:border-gray-600 rounded-xl px-4 py-3 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-secondary">
              <option disabled value="">Select Status</option>
              <option>Shorlisted</option>
              <option>Other</option>
              <!-- <option>Kept</option>
              <option>Processing</option> -->
            </select>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Applicant Type</label>
            <select v-model="newApplicant.entry_status" 
              class="w-full border border-gray-300 dark:border-gray-600 rounded-xl px-4 py-3 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-secondary">
              <option disabled value="">Select Type</option>
              <option value="Old">Old</option>
              <option value="New">New</option>
            </select>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Period</label>
            <select v-model="newApplicant.period" 
              class="w-full border border-gray-300 dark:border-gray-600 rounded-xl px-4 py-3 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-secondary">
              <option disabled value="">Select Period</option>
              <option value="First Term">First Term</option>
              <option value="Second Term">Second Term</option>
              <option value="Third Term">Third Term</option>
            </select>
          </div>
        </div>

        <!-- Interview Date & Time (New applicants only) -->
        <div v-if="newApplicant.entry_status === 'New'" class="grid grid-cols-1 md:grid-cols-2 gap-6 md:col-span-2">
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Interview Date</label>
            <input
              v-model="newApplicant.interview_date"
              type="date"
              required
              class="w-full border border-gray-300 dark:border-gray-600 rounded-xl px-4 py-3 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-secondary"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Interview Time</label>
            <input
              v-model="newApplicant.interview_time"
              type="time"
              required
              class="w-full border border-gray-300 dark:border-gray-600 rounded-xl px-4 py-3 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-secondary"
            />
          </div>
        </div>

        <!-- Recommendation -->
        <div class="md:col-span-2">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Recommendation Source</label>
          <select v-model="newApplicant.recommendation" 
            class="w-full border border-gray-300 dark:border-gray-600 rounded-xl px-4 py-3 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-secondary">
            <option disabled value="">Select Recommendation</option>
            <option value="agency">Agency</option>
            <option value="inward">Inward (Staff Referral)</option>
            <option value="other">Other</option>
          </select>
        </div>

        <!-- Agency Select -->
        <select
          v-if="newApplicant.recommendation === 'agency'"
          v-model="newApplicant.agency_id"
          class="md:col-span-2 w-full border border-gray-300 dark:border-gray-600 rounded-xl px-4 py-3 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-secondary"
        >
          <option disabled value="">Select Agency</option>
          <option v-for="agency in agencies" :key="agency.id" :value="agency.id">{{ agency.name }}</option>
        </select>

        <!-- Inward Staff Multiselect -->
        <div v-if="newApplicant.recommendation === 'inward'" class="md:col-span-2">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Referred By Staff</label>
          <Multiselect
            v-model="selectedStaff"
            :options="staffMembers"
            label="applicant_name"
            track-by="id"
            placeholder="Search and select staff member"
            class="w-full"
          />
        </div>

        <!-- CV Submission Section - Improved Design -->
<!-- CV Submission Section -->
<div class="md:col-span-2 bg-gray-50 dark:bg-gray-900/50 p-6 rounded-2xl border border-gray-200 dark:border-gray-700">
  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">CV Submission Method</label>

  <!-- Type Selection -->
  <select
    v-model="newApplicant.file_type"
    class="w-full border border-gray-300 dark:border-gray-600 rounded-xl px-4 py-3 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-secondary mb-4"
  >
    <option disabled value="">Choose how you want to submit the CV</option>
    <option value="file">📄 Upload PDF File</option>
    <option value="photo">📸 Take Photos of CV Pages (Live Camera)</option>
  </select>

  <!-- File Upload -->
  <div v-if="newApplicant.file_type === 'file'" class="space-y-3">
    <div class="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-2xl p-8 text-center hover:border-secondary">
      <input
        type="file"
        ref="cvInput"
        @change="handleFileUpload"
        accept=".pdf"
        class="hidden"
        id="cv-upload"
      />
      <label for="cv-upload" class="cursor-pointer flex flex-col items-center">
        <span class="text-4xl mb-3">📄</span>
        <span class="font-medium">Click to upload PDF</span>
        <span class="text-xs text-gray-500 mt-1">Only PDF files accepted</span>
      </label>
    </div>
  </div>

  <!-- Custom Live Camera Section -->
  <div v-if="newApplicant.file_type === 'photo'" class="space-y-4">
    <div class="text-sm font-medium text-gray-700 dark:text-gray-300">Live Camera Preview</div>

    <!-- Camera Container -->
    <div class="relative bg-black rounded-2xl overflow-hidden aspect-video border border-gray-300 dark:border-gray-600">
      <video
        ref="videoRef"
        autoplay
        playsinline
        class="w-full h-full object-cover"
      ></video>

      <!-- Overlay guidelines for CV -->
      <div class="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div class="border-2 border-white/70 border-dashed w-[85%] h-[85%] rounded-lg flex items-center justify-center">
          <span class="text-white/60 text-xs bg-black/50 px-3 py-1 rounded">Position CV here</span>
        </div>
      </div>

      <!-- Capture Button -->
      <button
      type="button"
        @click="capturePhoto"
        class="absolute bottom-6 left-1/2 -translate-x-1/2 bg-white text-black w-16 h-16 rounded-full flex items-center justify-center shadow-xl active:scale-95 transition-transform border-4 border-gray-800"
      >
        <span class="text-3xl">📸</span>
      </button>

      <!-- Switch Camera (if needed) -->
      <button
      type="button"
        @click="switchCamera"
        class="absolute top-4 right-4 bg-black/60 text-white px-4 py-2 rounded-full text-sm flex items-center gap-2"
      >
        🔄 Flip Camera
      </button>
    </div>

<!-- Captured Photos Preview -->
<div v-if="capturedPhotos && capturedPhotos.length > 0" class="space-y-3">
  <p class="text-sm font-medium text-gray-600 dark:text-gray-400">
    Captured Photos ({{ capturedPhotos.length }})
  </p>
  <div class="grid grid-cols-3 gap-3">
    <div v-for="(photo, index) in capturedPhotos" :key="index" class="relative">
      <img 
        :src="photo" 
        class="w-full h-24 object-cover rounded-lg border border-gray-300 dark:border-gray-600"
      />
      <!-- This is the fixed remove button -->
      <button
        @click="removePhoto(index)"
        class="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[10px] w-5 h-5 rounded-full flex items-center justify-center shadow hover:bg-red-600"
      >
        ✕
      </button>
    </div>
  </div>
</div>

    <p class="text-xs text-amber-600 dark:text-amber-400 text-center">
      On localhost this should open the camera directly. Take multiple photos of all CV pages.
    </p>
  </div>
</div>
<!-- File Preview -->
<div v-if="newApplicant.cv" 
     class="mt-4 p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl flex items-center justify-between">
  
  <div class="flex items-center gap-3">
    <span class="text-2xl">📄</span>

    <div>
      <p class="text-sm font-medium text-gray-700 dark:text-gray-200">
        {{ newApplicant.cv.name }}
      </p>
      <p class="text-xs text-gray-500">
        {{ (newApplicant.cv.size / 1024).toFixed(1) }} KB
      </p>
    </div>
  </div>

  <!-- Remove button -->
  <button
    @click="newApplicant.cv = null"
    class="text-red-500 hover:text-red-600 text-sm"
  >
    Remove
  </button>
</div>

        <!-- Comment -->
        <div class="md:col-span-2">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Additional Comments / Notes</label>
          <textarea
            v-model="newApplicant.comment"
            placeholder="Enter any additional information or reason for decision..."
            rows="4"
            class="w-full border border-gray-300 dark:border-gray-600 rounded-2xl px-4 py-3 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#035a76] resize-y"
          ></textarea>
        </div>

        <div v-if="isEditMode" class="md:col-span-2 bg-gray-50 dark:bg-gray-900/50 p-6 rounded-2xl border border-gray-200 dark:border-gray-700">
  <div class="flex items-center gap-2 mb-4">
    <span class="text-2xl">📎</span>
    <div>
      <h3 class="text-lg font-semibold text-gray-800 dark:text-white">Pre-Interview Attachments</h3>
      <p class="text-xs text-gray-500 dark:text-gray-400">Documents filled before interview (ID, Forms, Certificates, etc.)</p>
    </div>
  </div>

  <!-- Attachment Type Selection -->
  <select
    v-model="newApplicant.attachment_type"
    class="w-full border border-gray-300 dark:border-gray-600 rounded-xl px-4 py-3 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#035a76] mb-4"
  >
    <option disabled value="">Choose attachment method</option>
    <option value="file">📄 Upload PDF Files</option>
    <option value="photo">📸 Take Photos (Live Camera)</option>
  </select>

  <!-- File Upload -->
  <div v-if="newApplicant.attachment_type === 'file'" class="space-y-3">
    <div class="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-2xl p-8 text-center hover:border-[#035a76]">
      <input
        type="file"
        ref="attachmentInput"
        @change="handleAttachmentUpload"
        accept=".pdf,.jpg,.jpeg,.png"
        class="hidden"
        id="attachment-upload"
        multiple
      />
      <label for="attachment-upload" class="cursor-pointer flex flex-col items-center">
        <span class="text-4xl mb-3">📄</span>
        <span class="font-medium">Click to upload files</span>
        <span class="text-xs text-gray-500 mt-1">PDF, JPG, PNG accepted</span>
      </label>
    </div>
  </div>

  <!-- Live Camera Section -->
  <div v-if="newApplicant.attachment_type === 'photo'" class="space-y-4">
    <div class="text-sm font-medium text-gray-700 dark:text-gray-300">Live Camera Preview</div>

    <!-- Camera Container -->
    <div class="relative bg-black rounded-2xl overflow-hidden aspect-video border border-gray-300 dark:border-gray-600">
      <video
        ref="attachmentVideoRef"
        autoplay
        playsinline
        class="w-full h-full object-cover"
      ></video>

      <!-- Overlay guidelines -->
      <div class="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div class="border-2 border-white/70 border-dashed w-[85%] h-[85%] rounded-lg flex items-center justify-center">
          <span class="text-white/60 text-xs bg-black/50 px-3 py-1 rounded">Position document here</span>
        </div>
      </div>

      <!-- Capture Button -->
      <button
        type="button"
        @click="captureAttachmentPhoto"
        class="absolute bottom-6 left-1/2 -translate-x-1/2 bg-white text-black w-16 h-16 rounded-full flex items-center justify-center shadow-xl active:scale-95 transition-transform border-4 border-gray-800"
      >
        <span class="text-3xl">📸</span>
      </button>

      <!-- Switch Camera -->
      <button
        type="button"
        @click="switchAttachmentCamera"
        class="absolute top-4 right-4 bg-black/60 text-white px-4 py-2 rounded-full text-sm flex items-center gap-2"
      >
        🔄 Flip Camera
      </button>
    </div>

    <!-- Captured Photos Preview -->
    <div v-if="attachmentPhotos && attachmentPhotos.length > 0" class="space-y-3">
      <p class="text-sm font-medium text-gray-600 dark:text-gray-400">
        Captured Photos ({{ attachmentPhotos.length }})
      </p>
      <div class="grid grid-cols-3 gap-3">
        <div v-for="(photo, index) in attachmentPhotos" :key="index" class="relative">
          <img 
            :src="photo" 
            class="w-full h-24 object-cover rounded-lg border border-gray-300 dark:border-gray-600"
          />
          <button
            @click="removeAttachmentPhoto(index)"
            class="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[10px] w-5 h-5 rounded-full flex items-center justify-center shadow hover:bg-red-600"
          >
            ✕
          </button>
        </div>
      </div>
    </div>

    <p class="text-xs text-amber-600 dark:text-amber-400 text-center">
      Take photos of all pre-interview documents (ID, Forms, Certificates, etc.)
    </p>
  </div>

  <!-- Existing Attachments (if any) -->
  <div v-if="existingAttachments && existingAttachments.length > 0" class="mt-4 pt-4 border-t dark:border-gray-700">
    <p class="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
      Existing Attachments ({{ existingAttachments.length }})
    </p>
    <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
      <div v-for="(att, index) in existingAttachments" :key="index" class="relative group">
        <a 
          :href="att.file_path.startsWith('http') ? att.file_path : backendURL + att.file_path"
          target="_blank"
          class="block p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-[#035a76] transition text-center"
        >
          <span class="text-2xl">📄</span>
          <p class="text-xs text-gray-600 dark:text-gray-400 mt-1 truncate">
            {{ att.file_path.split('/').pop() }}
          </p>
        </a>
      </div>
    </div>
  </div>
</div>

        <!-- Submit Button -->
        <div class="md:col-span-2 flex justify-end pt-4">
<button
  v-if="hasPermission('manpower', 'shortlist')"
  type="submit" 
  :disabled="isLoading"
  class="bg-[#74bd54] hover:bg-[#63a43f] text-white px-10 py-3.5 rounded-2xl font-medium text-lg shadow-lg shadow-[#74bd54]/30 transition-all active:scale-95 flex items-center gap-2"
>
<!-- Spinner (show only when loading) -->
  <svg 
    v-if="isLoading" 
    class="animate-spin h-4 w-4 text-white" 
    xmlns="http://www.w3.org/2000/svg" 
    fill="none" 
    viewBox="0 0 24 24"
  >
    <circle 
      class="opacity-25" 
      cx="12" 
      cy="12" 
      r="10" 
      stroke="currentColor" 
      stroke-width="4"
    ></circle>
    <path 
      class="opacity-75" 
      fill="currentColor" 
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    ></path>
  </svg>

  <!-- Button text -->
<span>
  {{ isLoading 
      ? (isEditMode ? 'Updating...' : 'Saving...') 
      : (isEditMode ? 'Update Applicant' : 'Add Applicant') 
  }}
</span>
  <span class="text-xl">→</span>
</button>
        </div>

      </form>
    </div>
  </div>
</Transition>
          <!-- Filter Tabs -->
  
<div class="bg-white dark:bg-gray-800 shadow-lg rounded-2xl p-6 mb-6 border border-gray-100 dark:border-gray-700">

  <!-- Header -->
  <div class="flex justify-between items-center mb-6">
    <div>
      <h2 class="text-lg font-semibold text-gray-800 dark:text-white">
        Filter Applicants
      </h2>
      <p class="text-sm text-gray-500 dark:text-gray-400">
        Narrow down applicants using the filters below
      </p>
    </div>
  </div>

  <!-- Filters Grid -->
  <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">

    
    <!-- Status -->
    <div>
      <label class="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Status</label>
      <select v-model="filters.status" 
      class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2
       focus:ring-2 focus:ring-[#74bd54] focus:outline-none
       dark:bg-gray-700 dark:text-white transition">
        <option value="">All Status</option>
        <option v-for="tab in tabs" :key="tab" :value="tab">
          {{ tab }}
        </option>
      </select>
    </div>

    <!-- Position -->
    <div>
      <label class="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Position</label>
      <select v-model="filters.position" 
      class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2
       focus:ring-2 focus:ring-[#74bd54] focus:outline-none
       dark:bg-gray-700 dark:text-white transition">
        <option value="">All Positions</option>
        <option 
          v-for="pos in positions" 
          :key="pos.id" 
          :value="pos.position_name"
        >
          {{ pos.position_name }}
        </option>
      </select>
    </div>

    <!-- Period -->
    <div>
      <label class="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Period</label>
      <select v-model="filters.period" 
      class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2
       focus:ring-2 focus:ring-[#74bd54] focus:outline-none
       dark:bg-gray-700 dark:text-white transition">
        <option value="">All Terms</option>
        <option v-for="p in periods" :key="p" :value="p">
          {{ p }}
        </option>
      </select>
    </div>

    <!-- Month -->
    <div>
      <label class="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Month</label>
      <select v-model="selectedMonth" 
      class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2
       focus:ring-2 focus:ring-[#74bd54] focus:outline-none
       dark:bg-gray-700 dark:text-white transition">
        <option value="">All Months</option>
        <option v-for="m in months" :key="m.value" :value="m.value">
          {{ m.name }}
        </option>
      </select>
    </div>

    <!-- Year -->
    <div>
      <label class="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Year</label>
      <select v-model="selectedYear"
      class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2
       focus:ring-2 focus:ring-[#74bd54] focus:outline-none
       dark:bg-gray-700 dark:text-white transition">
        <option value="">All Years</option>
        <option v-for="y in years" :key="y" :value="y">
          {{ y }}
        </option>
      </select>
    </div>

    <!-- Search -->
    <div>
      <label class="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Search</label>
      <div class="relative">
        <input
          v-model="filters.search"
          type="text"
          placeholder="Search name or position..."
         class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2
       focus:ring-2 focus:ring-[#74bd54] focus:outline-none
       dark:bg-gray-700 dark:text-white transition"
        />
        <!-- Icon -->
        <svg class="w-4 h-4 absolute left-3 top-3 text-gray-400" fill="none" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-4.3-4.3m1.8-5.2a7 7 0 11-14 0 7 7 0 0114 0z"/>
        </svg>
      </div>
    </div>

  </div>

  <!-- Actions -->
  <div class="flex justify-between items-center mt-6 border-t pt-4 dark:border-gray-700">

    <!-- Left (optional future use) -->
    <div class="text-sm text-gray-500 dark:text-gray-400">
      {{ filteredApplicants.length }} results found
    </div>

    <!-- Buttons -->
    <div class="flex space-x-3">
      <button
        @click="resetFilters"
        class="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 
               text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
      >
        Reset
      </button>

      <button
        @click="applyFilters"
        class="px-6 py-2 rounded-lg bg-[#74bd54] text-white 
               shadow-md hover:shadow-lg hover:scale-[1.02] transition"
      >
        Apply Filters
      </button>
    </div>

  </div>
</div>

  
          <!-- Applicants Table -->
          <div class="overflow-x-auto bg-white dark:bg-gray-800 shadow rounded-lg">
            <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead class="bg-gray-100 dark:bg-gray-700">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">Name</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">Position</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">Status</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">Interview Period</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">Date</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">CV</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
                <tr v-for="applicant in filteredApplicants" :key="applicant.id">
                  <td class="px-6 py-4 text-sm text-gray-900 dark:text-white">{{ applicant.name }}</td>
                  <td class="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">{{ applicant.position }}</td>
                  <td class="px-6 py-4">
                    <span 
                      :class="{
                          'bg-blue-100 text-blue-800': applicant.status === 'Shortlisted',
                          'bg-blue-200 text-blue-900': applicant.status === 'Processing',
                          'bg-indigo-100 text-indigo-800': applicant.status === 'Interviewed',
                          'bg-amber-100 text-amber-800': applicant.status === 'Validated',
                          'bg-orange-100 text-orange-900': applicant.status === 'Contracting',

                          'bg-green-100 text-green-800': applicant.status === 'Hired',
                          'bg-red-100 text-red-800': applicant.status === 'Rejected',
                          'bg-gray-200 text-gray-800': applicant.status === 'Kept',
                          

                      }"
                      class="px-2 py-1 rounded-full text-xs font-medium"
                    >
                      {{ applicant.status }}
                    </span>
                  </td>
                  <td class="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">{{ applicant.period }} </td>
                  <td class="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">{{ applicant.date }}</td>
                <td class="px-6 py-4 space-y-2">
  <!-- PDF CV Link -->
  <a
    v-if="applicant.cv"
    :href="backendURL + applicant.cv"
    target="_blank"
    class="block text-blue-600 hover:underline dark:text-blue-400 text-sm"
  >
    📄 View PDF CV
  </a>
  
  <!-- Photos Link -->
  <a
    v-if="applicant.cv_photo_paths && applicant.cv_photo_paths.length > 0"
@click="viewPhotos(applicant)"
    href="#"
    class="block text-green-600 hover:underline dark:text-green-400 text-sm"
  >
    📸 View Photos ({{ applicant.cv_photo_paths.length }})
  </a>
  
  <!-- No CV -->
  <span v-if="!applicant.cv && (!applicant.cv_photo_paths || applicant.cv_photo_paths.length === 0)" 
        class="text-gray-400 text-sm">
    No CV
  </span>
</td>
                  <td class="px-6 py-4 flex space-x-2">
                  <router-link
                      v-if="hasPermission('manpower', 'interview')"
                    :to="`/interview/${applicant.id}`"
                    class="text-sm text-gray-600 hover:underline dark:text-gray-400"
                  >
                    Interview
                  </router-link>
                   
                    <button
                      v-if="hasPermission('manpower', 'validate')" 
                      @click="openActionModal(applicant, 'Validated')"
                      class="text-sm text-green-600 hover:underline dark:text-green-400"
                    >
                      Validate
                    </button>
                    <button
                      v-if="hasPermission('manpower', 'reject')" 
                      @click="openActionModal(applicant, 'Rejected')"
                      class="text-sm text-red-600 hover:underline dark:text-red-400"
                    >
                      Reject
                    </button>
                    <button
                      v-if="hasPermission('manpower', 'keep')" 
                      @click="openActionModal(applicant, 'Kept')"
                      class="text-sm text-yellow-600 hover:underline dark:text-yellow-400"
                    >
                      Keep
                    </button>
                    <button
                      v-if="hasPermission('manpower', 'process')" 
                      @click="openActionModal(applicant, 'Processing')"
                      class="text-sm text-blue-600 hover:underline dark:text-blue-400"
                    >
                      Process
                    </button>
                     <!-- Replace the existing Edit button -->
                    <button
                      v-if="hasPermission('manpower', 'edit')"
                      @click="editApplicant(applicant)"
                      class="text-sm text-[#035a76] hover:underline dark:text-blue-400"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
  
          <!-- Modals -->
          <Teleport to="body">
            <ActionModal 
              v-if="showActionModal"
              :applicant="selectedApplicant"
              :initialStatus="actionStatus"
              @close="showActionModal = false"
              @save="updateApplicantStatus"
            />
            <ViewModal 
              v-if="showViewModal"
              :applicant="selectedApplicant"
              @close="showViewModal = false"
            />
          </Teleport>

          <!-- Photos Modal -->
<Teleport to="body">
  <div 
    v-if="showPhotosModal" 
    class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
    @click.self="closePhotosModal"
  >
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
      
      <!-- Header -->
      <div class="flex justify-between items-center p-6 border-b dark:border-gray-700">
        <h3 class="text-xl font-semibold text-gray-800 dark:text-white">
          CV Photos - {{ modalApplicant?.name }}
        </h3>
        <button 
          @click="closePhotosModal"
          class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-2xl"
        >
          &times;
        </button>
      </div>
      
      <!-- Photos Grid -->
      <div class="p-6 grid grid-cols-2 md:grid-cols-3 gap-4">
        <div 
          v-for="(photo, index) in modalApplicant?.cv_photo_paths" 
          :key="index" 
          class="relative group cursor-pointer"
          @click="viewFullImage(photo)"
        >
          <img 
            :src="photo.startsWith('http') ? photo : backendURL + photo"
            class="w-full h-48 object-cover rounded-lg border border-gray-300 dark:border-gray-600"
            @error="console.log('Image load error:', photo)"
          />
          <div class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all rounded-lg flex items-center justify-center">
            <span class="text-white opacity-0 group-hover:opacity-100 text-sm font-medium">
              🔍 Click to enlarge
            </span>
          </div>
        </div>
      </div>
      
      <!-- Footer -->
      <div class="p-6 border-t dark:border-gray-700 flex justify-end">
        <button 
          @click="closePhotosModal"
          class="px-6 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition"
        >
          Close
        </button>
      </div>
    </div>
  </div>
  
  <!-- Full Image Viewer -->
  <div 
    v-if="fullImage" 
    class="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
    @click="closeFullImage"
  >
    <img 
      :src="fullImage" 
      class="max-w-full max-h-full object-contain rounded-lg"
      @click.stop
    />
    <button 
      @click="closeFullImage"
      class="absolute top-4 right-4 text-white text-4xl hover:text-gray-300"
    >
      &times;
    </button>
  </div>
</Teleport>
        </div>
      </main>
    </div>

    
  </template>
  
  <script setup>
  import { ref, computed, onMounted, watch, onUnmounted } from 'vue';
import Multiselect from 'vue-multiselect'
import 'vue-multiselect/dist/vue-multiselect.css'
  import api from "@/services/api";
  import HrSidebar from '@/components/hr/HrSidebar.vue';
  import HrTopbar from '@/components/hr/HrTopbar.vue';
  import ActionModal from '@/components/hr/ActionModal.vue';
  import ViewModal from '@/components/hr/ViewModal.vue';
  import { usePermissions } from '@/composables/usePermissions'

const { hasPermission } = usePermissions()

import { useToast } from 'vue-toastification'
const toast = useToast()
const isLoading = ref(false)

  
  const tabs = ['All', 'Hired', 'Rejected', 'Kept', 'Processing', 'Interviewed', 'Shortlisted', 'Validated'];
  const activeTab = ref('All');
  const searchQuery = ref('');
  const isSidebarOpen = ref(true);
  const isDarkMode = ref(false);
  const currentPageTitle = ref('Manpower Management');
  const showActionModal = ref(false);
  const showViewModal = ref(false);
  const selectedApplicant = ref(null);
  const actionStatus = ref('');

    const showFilter = ref(false);

    const positions = ref([]);

const cvInput = ref(null);

  const applicants = ref([]);

  const agencies = ref([]);

  const staffMembers = ref([]);

  const selectedStaff = ref(null)

  const isEditMode = ref(false);       
const editingApplicantId = ref(null);

  const videoRef = ref(null)
const currentStream = ref(null)
const capturedPhotos = ref([])
const facingMode = ref('environment') 

const showPhotosModal = ref(false);
const fullImage = ref(null);
const modalApplicant = ref(null); 

const attachmentInput = ref(null);
const attachmentVideoRef = ref(null);
const attachmentStream = ref(null);
const attachmentPhotos = ref([]);
const attachmentPhotoFiles = ref([]);
const attachmentFacingMode = ref('environment');
const existingAttachments = ref([]);


// Open photos modal
const viewPhotos = (applicant) => {
  modalApplicant.value = applicant;
  showPhotosModal.value = true;
};

// View full-size image
const viewFullImage = (photoUrl) => {
  // Ensure full URL
  fullImage.value = photoUrl.startsWith('http') 
    ? photoUrl 
    : backendURL + photoUrl;
};

// Close modals
const closePhotosModal = () => {
  showPhotosModal.value = false;
  modalApplicant.value = null;
};

const closeFullImage = () => {
  fullImage.value = null;
};

watch(selectedStaff, (val) => {
  newApplicant.value.inward_id = val ? val.id : ''
})



  // const backendURL = "http://localhost:8000/api/";
  const backendURL = import.meta.env.VITE_API_URL;
  

const filters = ref({
  status: '',
  position: '',
  period: '',
  date: '',
  search: ''
});

const periods = ref([
  'First Term',
  'Second Term',
  'Third Term'
]);

const selectedMonth = ref('');
const selectedYear = ref('');
  

const months = [
  { value: '01', name: 'January' },
  { value: '02', name: 'February' },
  { value: '03', name: 'March' },
  { value: '04', name: 'April' },
  { value: '05', name: 'May' },
  { value: '06', name: 'June' },
  { value: '07', name: 'July' },
  { value: '08', name: 'August' },
  { value: '09', name: 'September' },
  { value: '10', name: 'October' },
  { value: '11', name: 'November' },
  { value: '12', name: 'December' }
];

const years = computed(() => {
  const unique = new Set();

  applicants.value.forEach(app => {
    if (app.date) {
      const year = app.date.split('-')[0];
      unique.add(year);
    }
  });

  return Array.from(unique);
});
const resetFilters = () => {
  filters.value = {
    status: '',
    position: '',
    period: '',
    date: '',
    search: ''
  };
};


 const newApplicant = ref({
  name: '',
  position_id: '',
  email: '',
  phone: '',
  status: '',
  entry_status: '',
  period: '',
  recommendation: '',
  agency_id: '',
  inward_id: '',

  file_type: '',          
  cv: null,               
  cv_photos: [],           
  cv_photo_files: [],      

  comment: '',
  interview_date: '',    
    interview_time: '',
      attachment_type: '',
  attachment_files: [],
  attachment_photo_files: []
});

// Handle file upload
const handleAttachmentUpload = (e) => {
  const files = e.target.files;
  if (files && files.length > 0) {
    for (let i = 0; i < files.length; i++) {
      attachmentPhotoFiles.value.push(files[i]);
      newApplicant.value.attachment_files.push(files[i]);
    }
  }
};

// Start camera for attachments
async function startAttachmentCamera() {
  stopAttachmentCamera();
  
  try {
    const constraints = {
      video: {
        facingMode: attachmentFacingMode.value,
        width: { ideal: 1280 },
        height: { ideal: 720 }
      }
    };
    
    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    attachmentStream.value = stream;
    
    if (attachmentVideoRef.value) {
      attachmentVideoRef.value.srcObject = stream;
      attachmentVideoRef.value.onloadedmetadata = () => {
        attachmentVideoRef.value.play().catch(console.error);
      };
    }
  } catch (err) {
    console.error("Attachment camera error:", err);
    alert("Could not access camera for attachments.");
  }
}

// Stop attachment camera
function stopAttachmentCamera() {
  if (attachmentStream.value) {
    attachmentStream.value.getTracks().forEach(track => track.stop());
    attachmentStream.value = null;
  }
  if (attachmentVideoRef.value) {
    attachmentVideoRef.value.srcObject = null;
  }
}

// Switch camera for attachments
async function switchAttachmentCamera() {
  attachmentFacingMode.value = attachmentFacingMode.value === 'environment' ? 'user' : 'environment';
  await startAttachmentCamera();
}

// Capture photo for attachments
function captureAttachmentPhoto() {
  if (!attachmentVideoRef.value) return;
  
  const canvas = document.createElement('canvas');
  canvas.width = attachmentVideoRef.value.videoWidth || 1280;
  canvas.height = attachmentVideoRef.value.videoHeight || 720;
  
  const ctx = canvas.getContext('2d');
  ctx.drawImage(attachmentVideoRef.value, 0, 0, canvas.width, canvas.height);
  
  // For preview
  const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
  attachmentPhotos.value.push(dataUrl);
  
  // For upload
  canvas.toBlob((blob) => {
    if (blob) {
      const file = new File([blob], `attachment_${Date.now()}.jpg`, { type: 'image/jpeg' });
      attachmentPhotoFiles.value.push(file);
      newApplicant.value.attachment_photo_files.push(file);
    }
  }, 'image/jpeg', 0.92);
}

// Remove attachment photo
function removeAttachmentPhoto(index) {
  if (index >= 0 && index < attachmentPhotos.value.length) {
    attachmentPhotos.value.splice(index, 1);
    attachmentPhotoFiles.value.splice(index, 1);
    newApplicant.value.attachment_photo_files.splice(index, 1);
  }
}

// Watch for attachment_type change
watch(() => newApplicant.value?.attachment_type, (newVal) => {
  if (newVal === 'photo') {
    startAttachmentCamera();
  } else {
    stopAttachmentCamera();
    attachmentPhotos.value = [];
    attachmentPhotoFiles.value = [];
  }
});


  const fetchPositions = async () => {
  try {
    const response = await api.get("Positions.php");

    positions.value = response.data.map(position => ({
      id: position.id,
      department_id: position.department_id,
      department_name : position.department_name,
      position_name: position.title,
      code: position.code,
      description: position.description
    }));

  } catch (error) {
    console.error("Error fetching Positions:", error);
  }
}

const fetchAgencies = async () => {
  try{
    const response = await api.get("Agencies.php");
    agencies.value = response.data.map(agency => ({
      id:agency.id,
      name:agency.name,
      code:agency.code,
      description:agency.description
    }));

  } catch (error) {
    console.error("Error fetching Agencies:", error);
  }
};

const fetchApplicants = async () => {
  try {
    const response = await api.get("Applicants.php");
    
    applicants.value = response.data.map(applicant => ({
      id: applicant.id,
      name: applicant.full_name,
      email: applicant.email || '',
      phone: applicant.phone || '',
      position_id: applicant.position_id || '',
      position: applicant.position_title || '',
      status: applicant.applicant_status || '',
      entry_status: applicant.entry_status || '',
      period: applicant.period || '',
      comment: applicant.applicant_comment || '',
      date: applicant.created_at || '',
      cv: applicant.cv_file_path || '',
      
      // Recommendation
      recommendation: applicant.recommendation_type || '',
      agency_id: applicant.recommendation_type === 'agency' ? applicant.recommendation_id : '',
      inward_id: applicant.recommendation_type === 'inward' ? applicant.recommendation_id : '',
      recommendation_text: applicant.recommendation_text || '',
      
      interview_date: applicant.interview_date ? applicant.interview_date.split(' ')[0] : '',
      
      interview_time: applicant.interview_time?.substring(0, 5) || '',
      
      cv_type: applicant.cv_type || 'file',
      cv_photo_paths: applicant.cv_photo_paths || [],
      attachments: applicant.attachments || []
    }));

  } catch (error) {
    console.error("Error fetching Applicants:", error);
  }
}

const fetchInward = async () => {
  try {
    const response = await api.get('get_hired_applicants.php');

    console.log(response.data);

    staffMembers.value = response.data.data.map(item => ({
      id: item.applicant_id,
      applicant_name: item.applicant_name,
      full_name: item.full_name,
      email: item.email,
      position_title: item.position?.position_title,
      section_name: item.section?.section_name,
      class_name: item.class?.class_name,
      joining_date: item.contract?.joining_date,
      status: item.applicant_status
    }));

  } catch (error) {
    console.error('Error fetching staff members:', error);
  }
};

  const currentDate = computed(() => {
    return new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  });
  
const filteredApplicants = computed(() => {
  return applicants.value
    // .filter(applicant => applicant.entry_status === 'New')
    .filter(applicant => {

      const matchMonth =
        !selectedMonth.value ||
        applicant.date?.split('-')[1] === selectedMonth.value;

      const matchYear =
        !selectedYear.value ||
        applicant.date?.split('-')[0] === selectedYear.value;

      const matchPeriod =
        !filters.value.period ||
        applicant.period === filters.value.period;

      const matchPosition =
        !filters.value.position ||
        applicant.position === filters.value.position;

      const matchSearch =
        !filters.value.search ||
        applicant.name.toLowerCase().includes(filters.value.search.toLowerCase());

      return (
        matchMonth &&
        matchYear &&
        matchPeriod &&
        matchPosition &&
        matchSearch
      );
    });
});
  
  const countStatus = (status) => {
    return applicants.value.filter(a => a.status === status).length;
  };
  
  // const handleFileUpload = (e) => {
  //   const file = e.target.files[0];
  //   if (file) {
  //     newApplicant.value.cv = URL.createObjectURL(file); 
  //   }
  // };
  const handleFileUpload = (e) => {
  const file = e.target.files[0];

  if (file) {
    newApplicant.value.cv = file;  
  }
};
  
  // const addApplicant = () => {
  //   const id = Date.now();
  //   applicants.value.push({ id, ...newApplicant.value });
  //   newApplicant.value = { name: '', position: '', status: '', cv: '', comment: '' };
  // };

//  const editApplicant = (applicant) => {
//   // Show the form
//   showFilter.value = true;
  
//   // Set edit mode
//   isEditMode.value = true;
//   editingApplicantId.value = applicant.id;
  
//   // 🎯 Populate form fields - match your backend field names
//   newApplicant.value = {
//     id: applicant.id,
//     name: applicant.name || '',
//     position_id: applicant.position_id || '',
//     email: applicant.email || '',
//     phone: applicant.phone || '',
//     status: applicant.status || '',              // ← Make sure this matches backend
//     entry_status: applicant.entry_status || '',
//     period: applicant.period || '',
//     recommendation: applicant.recommendation || '',
//     agency_id: applicant.agency_id || '',
//     inward_id: applicant.inward_id || '',
//     file_type: applicant.cv_type || 'file',
//     cv: null, // Don't pre-fill file inputs
//     cv_photos: [],
//     cv_photo_files: [],
//     comment: applicant.comment || '',
    
//     // 🕐 Fix: Extract date/time in correct format for HTML inputs
//     interview_date: applicant.interview_date ? 
//       applicant.interview_date.split(' ')[0] : '',  
//     interview_time: applicant.interview_time ? 
//       applicant.interview_time.substring(0, 5) : ''  
//   };
  
//   // 👥 Handle inward staff selection if applicable
//   if (applicant.recommendation === 'inward' && applicant.inward_id) {
//     const staff = staffMembers.value.find(s => s.id == applicant.inward_id); // Use == for string/number match
//     if (staff) {
//       selectedStaff.value = staff;
//       newApplicant.value.inward_id = staff.id; // Ensure both are set
//     }
//   }

//   // 📸 Handle photo previews for edit mode
//   if (applicant.cv_type === 'photo' && applicant.cv_photo_paths?.length > 0) {
//     // Set form field
//     newApplicant.value.file_type = 'photo';
    
//     // Show previews in UI
//     capturedPhotos.value = applicant.cv_photo_paths.map(path => {
//       // Ensure full URL for image preview
//       return path.startsWith('http') ? path : backendURL + path;
//     });
//   }
  
//     if (applicant.attachments && applicant.attachments.length > 0) {
//     existingAttachments.value = applicant.attachments;
//   } else {
//     existingAttachments.value = [];
//   }
//   // 🎯 Scroll to form for better UX
//   setTimeout(() => {
//     document.querySelector('.space-y-8')?.scrollIntoView({ behavior: 'smooth' });
//   }, 100);

const editApplicant = (applicant) => {
  showFilter.value = true;
  isEditMode.value = true;
  editingApplicantId.value = applicant.id;
  
  // 🎯 Populate form fields
  newApplicant.value = {
    id: applicant.id,
    name: applicant.name || '',
    email: applicant.email || '',
    phone: applicant.phone || '',
    position_id: applicant.position_id || '',
    status: applicant.status || '',
    entry_status: applicant.entry_status || '',
    period: applicant.period || '',
    recommendation: applicant.recommendation || '',
    agency_id: applicant.agency_id || '',
    inward_id: applicant.inward_id || '',
    file_type: applicant.cv_type || 'file',
    cv: null,
    cv_photos: [],
    cv_photo_files: [],
    comment: applicant.comment || '',
    interview_date: applicant.interview_date?.split(' ')[0] || '',
    interview_time: applicant.interview_time?.substring(0, 5) || '',
    
    // ✅attachments:
    attachment_type: applicant.attachment_type || '',  
    attachment_files: [],       
    attachment_photo_files: []   
  };
  
  // 👥 Handle inward staff
  if (applicant.recommendation === 'inward' && applicant.inward_id) {
    const staff = staffMembers.value.find(s => s.id == applicant.inward_id);
    if (staff) {
      selectedStaff.value = staff;
      newApplicant.value.inward_id = staff.id;
    }
  }

  // 📸 Handle CV photo previews
  if (applicant.cv_type === 'photo' && applicant.cv_photo_paths?.length > 0) {
    newApplicant.value.file_type = 'photo';
    capturedPhotos.value = applicant.cv_photo_paths.map(path => {
      return path.startsWith('http') ? path : backendURL + path;
    });
  }
  
  // 📎 Handle EXISTING attachments (for display only)
  if (applicant.attachments && applicant.attachments.length > 0) {
    existingAttachments.value = applicant.attachments.map(att => ({
      ...att,
      // Ensure full URL for preview
      file_path: att.file_path.startsWith('http') ? att.file_path : backendURL + att.file_path
    }));
  } else {
    existingAttachments.value = [];
  }
  
  // Set attachment_type to show correct UI section
  if (applicant.attachments?.length > 0) {
    // If they had photo attachments, default to photo; otherwise file
    const hasPhotos = applicant.attachments.some(a => a.file_type === 'photo');
    newApplicant.value.attachment_type = hasPhotos ? 'photo' : 'file';
  }
  
  console.log("✅ Edit mode: Attachments loaded", {
    existingCount: existingAttachments.value.length,
    attachment_type: newApplicant.value.attachment_type
  });
  
  // Scroll to form
  setTimeout(() => {
    document.querySelector('.space-y-8')?.scrollIntoView({ behavior: 'smooth' });
  }, 100);

  
  // 🔍 Debug log (remove after testing)
  // console.log("✅ Edit mode activated:", {
  //   id: editingApplicantId.value,
  //   status: newApplicant.value.status,
  //   interview_time: newApplicant.value.interview_time,
  //   photoCount: capturedPhotos.value.length
  // });
};

// Function to cancel edit and reset form
const cancelEdit = () => {
  isEditMode.value = false;
  editingApplicantId.value = null;
  resetForm();
  showFilter.value = false;
};

// const addApplicant = async () => {
//   try {
//     const formData = new FormData();

//     // Basic fields
//     formData.append("name", newApplicant.value.name);
//     formData.append("email", newApplicant.value.email);
//     formData.append("phone", newApplicant.value.phone);
//     formData.append("position_id", newApplicant.value.position_id);
//     formData.append("status", newApplicant.value.status);
//     formData.append("entry_status", newApplicant.value.entry_status);
//     formData.append("period", newApplicant.value.period);

//     // Recommendation handling
//     let recommendation_type = newApplicant.value.recommendation;
//     let recommendation_id = null;
//     let recommendation_text = null;

//     if (recommendation_type === "agency") {
//       recommendation_id = newApplicant.value.agency_id;
//     } 
//     else if (recommendation_type === "inward") {
//       recommendation_id = newApplicant.value.inward_id || (selectedStaff?.value?.id || null);
//     } 
//     else if (recommendation_type === "other") {
//       recommendation_text = newApplicant.value.comment;
//     }

//     formData.append("recommendation_type", recommendation_type || "");
//     formData.append("recommendation_id", recommendation_id ?? "");
//     formData.append("recommendation_text", recommendation_text ?? "");

//     formData.append("comment", newApplicant.value.comment);

//     // ====================== NEW FIELDS ======================
//     formData.append("cv_type", newApplicant.value.file_type || "file");

//     // Send interview date & time only if entry_status is "New"
//     if (newApplicant.value.entry_status === 'New') {
//       formData.append("interview_date", newApplicant.value.interview_date || "");
//       formData.append("interview_time", newApplicant.value.interview_time || "");
//     }

//     // ====================== CV HANDLING ======================
//     if (newApplicant.value.file_type === 'file' && newApplicant.value.cv) {
//       formData.append("cv", newApplicant.value.cv);
//     } 
//     else if (newApplicant.value.file_type === 'photo' && newApplicant.value.cv_photo_files?.length > 0) {
//       // Append multiple photos
//       newApplicant.value.cv_photo_files.forEach((file, index) => {
//         formData.append(`cv_photos[${index}]`, file);
//       });
//     }

//     // Send to backend
//     const response = await api.post("Applicants.php", formData);

//     console.log("Response:", response.data);   // For debugging

//     // Temporary debug - remove after testing
// console.log("Files being sent:", newApplicant.value.cv_photo_files.length);

//     // Add to list
//     applicants.value.push({
//       id: response.data.id,
//       name: newApplicant.value.name,
//       email: newApplicant.value.email,
//       phone: newApplicant.value.phone,
//       position_id: newApplicant.value.position_id,
//       status: newApplicant.value.status,
//       entry_status: newApplicant.value.entry_status,
//       period: newApplicant.value.period,

//       recommendation_type,
//       recommendation_id,
//       recommendation_text,

//       cv: response.data.cv || null,
//       cv_type: newApplicant.value.file_type,
//       comment: newApplicant.value.comment
//     });

//     // Reset form
//     resetForm();

//     alert("Applicant added successfully!");   // Optional feedback

//   } catch (error) {
//     console.error("Error adding applicant:", error);
//     alert("Failed to add applicant. Please check console.");
//   }
// };
const addApplicant = async () => {
  if (isLoading.value) return;
  isLoading.value = true;

  const MIN_LOADING_MS = 2000;
  const startTime = Date.now();

  // Dynamic loading message
  const loadingMessage = isEditMode.value 
    ? "Updating applicant..." 
    : "Saving applicant...";

  let loadingToastId = toast.info(loadingMessage, { timeout: false });

  try {
    const formData = new FormData();

    formData.append("name", newApplicant.value.name);
    formData.append("email", newApplicant.value.email);
    formData.append("phone", newApplicant.value.phone);
    formData.append("position_id", newApplicant.value.position_id);
    formData.append("status", newApplicant.value.status);
    formData.append("entry_status", newApplicant.value.entry_status);
    formData.append("period", newApplicant.value.period);

    let recommendation_type = newApplicant.value.recommendation;
    let recommendation_id = null;
    let recommendation_text = null;

    if (recommendation_type === "agency") {
      recommendation_id = newApplicant.value.agency_id;
    } else if (recommendation_type === "inward") {
      recommendation_id = newApplicant.value.inward_id || (selectedStaff?.value?.id || null);
    } else if (recommendation_type === "other") {
      recommendation_text = newApplicant.value.comment;
    }

    formData.append("recommendation_type", recommendation_type || "");
    formData.append("recommendation_id", recommendation_id ?? "");
    formData.append("recommendation_text", recommendation_text ?? "");
    formData.append("comment", newApplicant.value.comment || "");

    formData.append("cv_type", newApplicant.value.file_type || "file");

    if (newApplicant.value.entry_status === 'New') {
      formData.append("interview_date", newApplicant.value.interview_date || "");
      formData.append("interview_time", newApplicant.value.interview_time || "");
    }

    if (newApplicant.value.file_type === 'file' && newApplicant.value.cv) {
      formData.append("cv", newApplicant.value.cv);
    } else if (newApplicant.value.file_type === 'photo' && newApplicant.value.cv_photo_files?.length > 0) {
      newApplicant.value.cv_photo_files.forEach((file, index) => {
        formData.append(`cv_photos[${index}]`, file);
      });
    }

    formData.append("attachment_type", newApplicant.value.attachment_type || "");

    if (newApplicant.value.attachment_type === 'file' && newApplicant.value.attachment_files?.length > 0) {
      newApplicant.value.attachment_files.forEach((file, index) => {
        formData.append(`attachment_files[${index}]`, file);
      });
    } else if (newApplicant.value.attachment_type === 'photo' && attachmentPhotoFiles.value?.length > 0) {
      attachmentPhotoFiles.value.forEach((file, index) => {
        formData.append(`attachment_photos[${index}]`, file);
      });
    }

    let response;

    // ===== EDIT MODE =====
    if (isEditMode.value && editingApplicantId.value) {
      formData.append("id", editingApplicantId.value);
      formData.append("_method", "PUT");

      if (existingAttachments.value?.length > 0) {
        existingAttachments.value.forEach((att, index) => {
          formData.append(`existing_attachment_ids[${index}]`, att.id);
        });
      }

      response = await api.post("Applicants.php", formData);

      if (!response.data.success) {
        throw new Error(response.data.message || "Failed to update applicant");
      }

      // Update local state
      const index = applicants.value.findIndex(a => a.id === editingApplicantId.value);
      if (index !== -1) {
        applicants.value[index] = {
          ...applicants.value[index],
          name: newApplicant.value.name,
          email: newApplicant.value.email,
          phone: newApplicant.value.phone,
          position_id: newApplicant.value.position_id,
          position: positions.value.find(p => p.id == newApplicant.value.position_id)?.position_name || applicants.value[index].position,
          status: newApplicant.value.status,
          period: newApplicant.value.period,
          comment: newApplicant.value.comment,
          interview_date: newApplicant.value.interview_date,
          interview_time: newApplicant.value.interview_time
        };
      }

      toast.success("Applicant updated successfully!");

      fetchApplicants();

    } else {
      // ===== CREATE =====
      response = await api.post("Applicants.php", formData);

      if (!response.data.id) {
        throw new Error(response.data.message || "Failed to create applicant");
      }

      applicants.value.unshift({
        id: response.data.id,
        name: newApplicant.value.name,
        email: newApplicant.value.email,
        phone: newApplicant.value.phone,
        position_id: newApplicant.value.position_id,
        position: positions.value.find(p => p.id == newApplicant.value.position_id)?.position_name || '',
        status: newApplicant.value.status,
        entry_status: newApplicant.value.entry_status,
        period: newApplicant.value.period,
        comment: newApplicant.value.comment,
        date: new Date().toISOString().split('T')[0],
        cv: response.data.cv || null,
        recommendation: recommendation_type,
        agency_id: recommendation_type === 'agency' ? recommendation_id : '',
        inward_id: recommendation_type === 'inward' ? recommendation_id : '',
        recommendation_text: recommendation_text,
        interview_date: newApplicant.value.interview_date,
        interview_time: newApplicant.value.interview_time,
        cv_type: newApplicant.value.file_type,
        cv_photo_paths: []
      });

      toast.success("Applicant added successfully!");
            fetchApplicants();

    }

    // ===== MIN DELAY =====
    const elapsed = Date.now() - startTime;
    const remaining = Math.max(0, MIN_LOADING_MS - elapsed);
    if (remaining > 0) await new Promise(res => setTimeout(res, remaining));

    toast.dismiss(loadingToastId);

    // Cleanup
    resetForm();
    isEditMode.value = false;
    editingApplicantId.value = null;
    showFilter.value = false;

  } catch (error) {
    const elapsed = Date.now() - startTime;
    const remaining = Math.max(0, MIN_LOADING_MS - elapsed);
    if (remaining > 0) await new Promise(res => setTimeout(res, remaining));

    const errorMsg =
      error.response?.data?.message ||
      error.message ||
      "An unexpected error occurred";

    toast.dismiss(loadingToastId);
    toast.error(errorMsg);

  } finally {
    isLoading.value = false;
  }
};
// Helper function to reset the form cleanly
// const addApplicant = async () => {
//   try {
//     const formData = new FormData();

//     // Basic fields
//     formData.append("name", newApplicant.value.name);
//     formData.append("email", newApplicant.value.email);
//     formData.append("phone", newApplicant.value.phone);
//     formData.append("position_id", newApplicant.value.position_id);
//     formData.append("status", newApplicant.value.status);
//     formData.append("entry_status", newApplicant.value.entry_status);
//     formData.append("period", newApplicant.value.period);

//     // Recommendation handling
//     let recommendation_type = newApplicant.value.recommendation;
//     let recommendation_id = null;
//     let recommendation_text = null;

//     if (recommendation_type === "agency") {
//       recommendation_id = newApplicant.value.agency_id;
//     } else if (recommendation_type === "inward") {
//       recommendation_id = newApplicant.value.inward_id || (selectedStaff?.value?.id || null);
//     } else if (recommendation_type === "other") {
//       recommendation_text = newApplicant.value.comment;
//     }

//     formData.append("recommendation_type", recommendation_type || "");
//     formData.append("recommendation_id", recommendation_id ?? "");
//     formData.append("recommendation_text", recommendation_text ?? "");
//     formData.append("comment", newApplicant.value.comment || "");

//     // CV Type & Interview fields
//     formData.append("cv_type", newApplicant.value.file_type || "file");
    
//     if (newApplicant.value.entry_status === 'New') {
//       formData.append("interview_date", newApplicant.value.interview_date || "");
//       formData.append("interview_time", newApplicant.value.interview_time || "");
//     }

//     // CV File handling
//     if (newApplicant.value.file_type === 'file' && newApplicant.value.cv) {
//       formData.append("cv", newApplicant.value.cv);
//     } else if (newApplicant.value.file_type === 'photo' && newApplicant.value.cv_photo_files?.length > 0) {
//       newApplicant.value.cv_photo_files.forEach((file, index) => {
//         formData.append(`cv_photos[${index}]`, file);
//       });
//     }

//      formData.append("attachment_type", newApplicant.value.attachment_type || "");
    
//     if (newApplicant.value.attachment_type === 'file' && newApplicant.value.attachment_files?.length > 0) {
//       newApplicant.value.attachment_files.forEach((file, index) => {
//         formData.append(`attachment_files[${index}]`, file);
//       });
//     } else if (newApplicant.value.attachment_type === 'photo' && attachmentPhotoFiles.value?.length > 0) {
//       attachmentPhotoFiles.value.forEach((file, index) => {
//         formData.append(`attachment_photos[${index}]`, file);
//       });
//     }

//     let response;
    
//     // 🔄 CHECK IF EDIT MODE - Use PUT for update, POST for create
//    if (isEditMode.value && editingApplicantId.value) {
//   // ✅ Append ID to FormData (so PHP can read $_POST['id'])
//   formData.append("id", editingApplicantId.value);
  
//   // ✅ Use _method override for PUT
//   formData.append("_method", "PUT");

//   if (existingAttachments.value?.length > 0) {
//     existingAttachments.value.forEach((att, index) => {
//       formData.append(`existing_attachment_ids[${index}]`, att.id);
//     });
//   }
  
//   // ✅ Send to base endpoint (not with ID in URL)
//   response = await api.post("Applicants.php", formData);
  
//   // Update local list only if backend confirms success
//   if (response.data.success) {
//     const index = applicants.value.findIndex(a => a.id === editingApplicantId.value);
//     if (index !== -1) {
//       applicants.value[index] = {
//         ...applicants.value[index],
//         name: newApplicant.value.name,
//         email: newApplicant.value.email,
//         phone: newApplicant.value.phone,
//         position_id: newApplicant.value.position_id,
//         position: positions.value.find(p => p.id == newApplicant.value.position_id)?.position_name || applicants.value[index].position,
//         status: newApplicant.value.status,
//         period: newApplicant.value.period,
//         comment: newApplicant.value.comment,
//         interview_date: newApplicant.value.interview_date,
//         interview_time: newApplicant.value.interview_time
//       };
//     }
//     alert("✅ Applicant updated successfully!");
//   } else {
//     alert("❌ Update failed: " + (response.data.message || "Unknown error"));
//   }

      
  
//    } else {
//   // Create new applicant
//   response = await api.post("Applicants.php", formData);
  
//   // ✅ Push complete object matching fetchApplicants structure
//   applicants.value.unshift({  // unshift adds to top of list
//     id: response.data.id,
//     name: newApplicant.value.name,
//     email: newApplicant.value.email,
//     phone: newApplicant.value.phone,
//     position_id: newApplicant.value.position_id,
//     position: positions.value.find(p => p.id == newApplicant.value.position_id)?.position_name || '',
//     status: newApplicant.value.status,
//     entry_status: newApplicant.value.entry_status,
//     period: newApplicant.value.period,
//     comment: newApplicant.value.comment,
//     date: new Date().toISOString().split('T')[0], // Current date
//     cv: response.data.cv || null,
    
//     // Recommendation
//     recommendation: recommendation_type,
//     agency_id: recommendation_type === 'agency' ? recommendation_id : '',
//     inward_id: recommendation_type === 'inward' ? recommendation_id : '',
//     recommendation_text: recommendation_text,
    
//     // Interview
//     interview_date: newApplicant.value.interview_date,
//     interview_time: newApplicant.value.interview_time,
//     cv_type: newApplicant.value.file_type,
//     cv_photo_paths: []
//   });
  
//   alert("✅ Applicant added successfully!");
// }

//     // Reset and exit edit mode
//     resetForm();
//     isEditMode.value = false;
//     editingApplicantId.value = null;
//     showFilter.value = false; 

//   } catch (error) {
//     console.error("Error saving applicant:", error);
//     alert("Failed to save applicant. Please check console.");
//   }
// };

// const resetForm = () => {
//   newApplicant.value = {
//     name: '',
//     position_id: '',
//     email: '',
//     phone: '',
//     status: '',
//     entry_status: '',
//     period: '',
//     recommendation: '',
//     agency_id: '',
//     inward_id: '',
//     file_type: '',
//     cv: null,
//     cv_photos: [],
//     cv_photo_files: [],
//     comment: '',
//     interview_date: '',     // ← Add this
//     interview_time: ''      // ← Add this
//   };

//   selectedStaff.value = null;
//   capturedPhotos.value = [];   // Clear preview photos

//   // Clear file inputs
//   if (cvInput.value) cvInput.value.value = "";
// };
  
const resetForm = () => {
  newApplicant.value = {
    name: '',
    position_id: '',
    email: '',
    phone: '',
    status: '',
    entry_status: '',
    period: '',
    recommendation: '',
    agency_id: '',
    inward_id: '',
    file_type: '',
    cv: null,
    cv_photos: [],
    cv_photo_files: [],
    comment: '',
    interview_date: '',
    interview_time: '',
    attachment_type: '',
    attachment_files: [],
     attachment_photo_files: []
  };

  selectedStaff.value = null;
  capturedPhotos.value = [];
  
  if (cvInput.value) cvInput.value.value = "";
  
  // Reset edit state
  isEditMode.value = false;
  editingApplicantId.value = null;
};
  const openActionModal = (applicant, status) => {
    selectedApplicant.value = { ...applicant };
    actionStatus.value = status;
    showActionModal.value = true;
  };
  
  const openViewModal = (applicant) => {
    selectedApplicant.value = { ...applicant };
    showViewModal.value = true;
  };
  
 const updateApplicantStatus = async ({ applicant, status, comment }) => {
  const MIN_LOADING_MS = 1500;
  const startTime = Date.now();

  const toastId = toast.info("Updating status...", { timeout: false });

  try {
    // 1️⃣ Backend call
    const res = await api.put("Applicants.php", {
      id: applicant.id,
      status,
      comment
    });

    // 2️⃣ Handle backend error (important!)
    if (res.data?.status === "error") {
      throw new Error(res.data.message || "Failed to update status");
    }

    // 3️⃣ Update local state
    const index = applicants.value.findIndex(a => a.id === applicant.id);
    if (index !== -1) {
      applicants.value[index] = {
        ...applicants.value[index],
        status,
        comment
      };
    }

    // 4️⃣ Success toast
    toast.success("Status updated successfully!");

    // 5️⃣ Close modal
    showActionModal.value = false;
    selectedApplicant.value = null;

  } catch (error) {
    console.error("Failed to update applicant status:", error);

    const errorMsg =
      error.response?.data?.message ||
      error.message ||
      "Failed to update status";

    toast.error(errorMsg);

  } finally {
    // 6️⃣ Minimum delay (same UX consistency)
    const elapsed = Date.now() - startTime;
    const remaining = Math.max(0, MIN_LOADING_MS - elapsed);
    if (remaining > 0) await new Promise(res => setTimeout(res, remaining));

    // 7️⃣ Dismiss loading toast
    toast.dismiss(toastId);
  }
};

//   const updateApplicantStatus = async ({ applicant, status, comment }) => {
//   try {
//     // 1️⃣ Call the backend to update status/comment
//     await api.put("Applicants.php", {
//       id: applicant.id,
//       status,
//       comment
//     });

//     // 2️⃣ Update local table after success
//     const index = applicants.value.findIndex(a => a.id === applicant.id);
//     if (index !== -1) {
//       applicants.value[index] = {
//         ...applicants.value[index],
//         status,
//         comment
//       };
//     }

//     // 3️⃣ Close modal & reset selected applicant
//     showActionModal.value = false;
//     selectedApplicant.value = null;

//   } catch (error) {
//     console.error("Failed to update applicant status:", error);
//     // Optional: show error to user
//   }
// };
  
  const toggleSidebar = () => {
    isSidebarOpen.value = !isSidebarOpen.value;
  };
  
  const toggleDarkMode = () => {
    isDarkMode.value = !isDarkMode.value;
    if (isDarkMode.value) {
      document.documentElement.classList.add('dark');
      localStorage.theme = 'dark';
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.theme = 'light';
    }
  };
  
  const refreshData = () => {
    console.log('Refreshing data...');
    // Implement data refresh logic (e.g., API call)
  };



  // start with back camera

function removePhoto(index) {
  if (index >= 0 && index < capturedPhotos.value.length) {
    capturedPhotos.value.splice(index, 1)
    newApplicant.value.cv_photo_files.splice(index, 1)
  }
}

async function startCamera() {
  stopCamera() // stop any previous stream

  try {
    // Much more flexible constraints - this fixes OverconstrainedError in most cases
    const constraints = {
      video: {
        facingMode: facingMode.value,           // changed: no "exact" initially
        width: { ideal: 1280 },
        height: { ideal: 720 }
      }
    }

    const stream = await navigator.mediaDevices.getUserMedia(constraints)
    currentStream.value = stream

    if (videoRef.value) {
      videoRef.value.srcObject = stream
      // Optional: wait a bit for video to load dimensions
      videoRef.value.onloadedmetadata = () => {
        videoRef.value.play().catch(console.error)
      }
    }
  } catch (err) {
    console.error("Camera error:", err.name, err.message)

    if (err.name === 'OverconstrainedError') {
      // Fallback: try with minimal constraints
      console.log("Trying minimal constraints...")
      try {
        const fallbackConstraints = {
          video: { facingMode: facingMode.value }
        }
        const stream = await navigator.mediaDevices.getUserMedia(fallbackConstraints)
        currentStream.value = stream
        if (videoRef.value) videoRef.value.srcObject = stream
      } catch (fallbackErr) {
        console.error("Fallback also failed:", fallbackErr)
        alert("Could not access the camera. Please make sure camera permission is allowed.")
      }
    } else if (err.name === 'NotAllowedError') {
      alert("Camera access was denied. Please allow camera permission in your browser.")
    } else if (err.name === 'NotFoundError') {
      alert("No camera found on this device.")
    } else {
      alert("Camera error: " + err.message)
    }
  }
}

function stopCamera() {
  if (currentStream.value) {
    currentStream.value.getTracks().forEach(track => track.stop())
    currentStream.value = null
  }
  if (videoRef.value) {
    videoRef.value.srcObject = null
  }
}

async function switchCamera() {
  facingMode.value = facingMode.value === 'environment' ? 'user' : 'environment'
  await startCamera()
}

// Capture function remains the same
function capturePhoto() {
  if (!videoRef.value) return

  const canvas = document.createElement('canvas')
  canvas.width = videoRef.value.videoWidth || 1280
  canvas.height = videoRef.value.videoHeight || 720

  const ctx = canvas.getContext('2d')
  ctx.drawImage(videoRef.value, 0, 0, canvas.width, canvas.height)

  // For preview
  const dataUrl = canvas.toDataURL('image/jpeg', 0.92)
  capturedPhotos.value.push(dataUrl)

  // For upload - This was missing or broken
  canvas.toBlob((blob) => {
    if (blob) {
      const file = new File([blob], `cv_photo_${Date.now()}.jpg`, { 
        type: 'image/jpeg' 
      })
      newApplicant.value.cv_photo_files.push(file)
    }
  }, 'image/jpeg', 0.92)
}
// Watch for file_type change (keep this)
watch(() => newApplicant.value?.file_type, (newVal) => {
  if (newVal === 'photo') {
    startCamera()
  } else {
    stopCamera()
    capturedPhotos.value = []
    newApplicant.value.cv_photo_files = []
  }
})

onUnmounted(() => {
  stopCamera();
    stopAttachmentCamera(); 

})

  onMounted(() => {
fetchPositions();
fetchApplicants();
fetchAgencies();
fetchInward();
  });
  </script>
  <style>
filter-enter-active,
.filter-leave-active {
  transition: all 0.35s ease;
}

.filter-enter-from,
.filter-leave-to {
  opacity: 0;
  transform: translateY(-15px);
  max-height: 0;
}

.filter-enter-to,
.filter-leave-from {
  opacity: 1;
  transform: translateY(0);
  max-height: 1000px;
}

</style>